export const CHAT_DRAFT_CHANGED_EVENT = "chat-draft-changed";

export interface ChatDraft {
    userId: string;
    conversationId: number;
    threadRootMessageId?: number;
    plaintext: string;
    doc: unknown;
    updatedAt: number;
}

const STORAGE_PREFIX = "sprout:chat-draft:v1";

const storageAvailable = (): boolean => {
    try { return typeof window !== "undefined" && Boolean(window.localStorage); }
    catch { return false; }
};

const draftKey = (userId: string, conversationId: number, threadRootMessageId?: number): string =>
    `${STORAGE_PREFIX}:${encodeURIComponent(userId)}:${conversationId}:${threadRootMessageId ?? "conversation"}`;

const dispatchDraftChanged = (draft: Pick<ChatDraft, "userId" | "conversationId" | "threadRootMessageId">): void => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(CHAT_DRAFT_CHANGED_EVENT, { detail: draft }));
};

export function saveChatDraft(draft: ChatDraft): boolean {
    if (!storageAvailable()) return false;
    if (!draft.plaintext.trim()) {
        return clearChatDraft(draft.userId, draft.conversationId, draft.threadRootMessageId, draft.updatedAt);
    }
    try {
        window.localStorage.setItem(draftKey(draft.userId, draft.conversationId, draft.threadRootMessageId), JSON.stringify(draft));
        dispatchDraftChanged(draft);
        return true;
    } catch (error) {
        console.warn("Unable to persist chat draft.", error);
        return false;
    }
}

export function getChatDraft(userId: string, conversationId: number, threadRootMessageId?: number): ChatDraft | null {
    if (!storageAvailable() || !userId || conversationId <= 0) return null;
    try {
        const value = window.localStorage.getItem(draftKey(userId, conversationId, threadRootMessageId));
        if (!value) return null;
        const draft = JSON.parse(value) as ChatDraft;
        return draft?.userId === userId && draft?.conversationId === conversationId ? draft : null;
    } catch {
        return null;
    }
}

export function clearChatDraft(userId: string, conversationId: number, threadRootMessageId?: number, revision = nextChatDraftRevision()): boolean {
    if (!storageAvailable()) return false;
    try {
        const key = draftKey(userId, conversationId, threadRootMessageId);
        window.localStorage.removeItem(key);
        clearedDraftRevisions.set(key, revision);
        dispatchDraftChanged({ userId, conversationId, threadRootMessageId });
        return true;
    } catch { return false; }
}

export function getConversationDraftPreview(userId: string, conversationId: number): string | null {
    const draft = getChatDraft(userId, conversationId);
    const text = draft?.plaintext.trim().replace(/\s+/g, " ");
    return text || null;
}

// Capture revision at edit time, not flush time: multiple mounted editors may
// flush in any listener order. The counter also orders edits in the same ms.
const clearedDraftRevisions = new Map<string, number>();
let lastDraftRevision = 0;
export function nextChatDraftRevision(): number {
    lastDraftRevision = Math.max(Date.now(), lastDraftRevision + 1);
    return lastDraftRevision;
}
export function savePendingChatDraft(draft: ChatDraft): boolean {
    const current = getChatDraft(draft.userId, draft.conversationId, draft.threadRootMessageId);
    const clearedAt = clearedDraftRevisions.get(draftKey(draft.userId, draft.conversationId, draft.threadRootMessageId)) ?? 0;
    if (Math.max(current?.updatedAt ?? 0, clearedAt) > draft.updatedAt) return true;
    return saveChatDraft(draft);
}
